import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Eye, Save, Rocket, RotateCcw, ChevronDown, Search } from "lucide-react";
import { invalidatePage } from "@/hooks/usePageContent";
import useSettings from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
    { slug: "home", label: "Accueil", url: "/" },
    { slug: "matelas", label: "Matelas", url: "/matelas" },
];
const LOCALE_LABELS = { fr: "Français", nl: "Nederlands", en: "English" };

export default function CMSEditor() {
    const [slug, setSlug] = useState("home");
    const [locale, setLocale] = useState("fr");
    const [data, setData] = useState(null);
    const [values, setValues] = useState({});
    const [seoValues, setSeoValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    const [showSeo, setShowSeo] = useState(false);
    const settings = useSettings();
    const availableLocales = ["fr", "nl", "en"].filter((l) => settings.languages?.[l] !== false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await axios.get(`${API}/admin/pages/${slug}?locale=${locale}`, { withCredentials: true });
            setData(d);
            setValues({ ...d.defaults, ...(d.content || {}), ...(d.draft || {}) });
            setSeoValues({ ...d.seo_defaults, ...(d.seo || {}), ...(d.seo_draft || {}) });
            setOpenGroup(d.schema[0]?.group || null);
        } catch {
            toast.error("Chargement impossible.");
        } finally {
            setLoading(false);
        }
    }, [slug, locale]);

    useEffect(() => { load(); }, [load]);

    const groups = useMemo(() => {
        if (!data) return [];
        const seen = new Set();
        const order = [];
        for (const f of data.schema) {
            if (!seen.has(f.group)) { seen.add(f.group); order.push(f.group); }
        }
        return order.map((g) => ({ name: g, fields: data.schema.filter((f) => f.group === g) }));
    }, [data]);

    const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));
    const setSeoField = (key, val) => setSeoValues((v) => ({ ...v, [key]: val }));

    const isChanged = (key) => {
        if (!data) return false;
        const pub = (data.content && data.content[key]) ?? data.defaults[key];
        return values[key] !== pub;
    };
    const isSeoChanged = (key) => {
        if (!data) return false;
        const pub = (data.seo && data.seo[key]) ?? data.seo_defaults[key];
        return seoValues[key] !== pub;
    };
    const anyChanged = data && (data.schema.some((f) => isChanged(f.key)) || (data.seo_schema || []).some((f) => isSeoChanged(f.key)));

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const payload = {};
            for (const f of data.schema) if (values[f.key] !== undefined) payload[f.key] = values[f.key];
            const seoPayload = {};
            for (const f of (data.seo_schema || [])) if (seoValues[f.key] !== undefined) seoPayload[f.key] = seoValues[f.key];
            await axios.put(`${API}/admin/pages/${slug}`, { locale, data: payload, seo: seoPayload }, { withCredentials: true });
            toast.success("Brouillon enregistré.");
            invalidatePage(slug);
            await load();
        } catch {
            toast.error("Enregistrement impossible.");
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (publishing) return;
        if (!window.confirm("Publier ces modifications sur le site public ?")) return;
        setPublishing(true);
        try {
            await save();
            await axios.post(`${API}/admin/pages/${slug}/publish`, {}, { withCredentials: true });
            toast.success("Modifications publiées.");
            invalidatePage(slug);
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Publication impossible.";
            toast.error(typeof msg === "string" ? msg : "Publication impossible.");
        } finally {
            setPublishing(false);
        }
    };

    const discard = async () => {
        if (!window.confirm("Abandonner toutes les modifications non publiées ?")) return;
        try {
            await axios.post(`${API}/admin/pages/${slug}/discard`, {}, { withCredentials: true });
            toast.success("Modifications abandonnées.");
            invalidatePage(slug);
            await load();
        } catch {
            toast.error("Opération impossible.");
        }
    };

    const openPreview = async () => {
        await save();
        const base = PAGES.find((p) => p.slug === slug)?.url || "/";
        const target = locale === "fr" ? base : `/${locale}${base === "/" ? "" : base}`;
        window.open(`${target}?preview=1`, "_blank", "noopener");
    };

    if (loading && !data) {
        return <div className="flex items-center gap-2 text-[#64748B]"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;
    }

    return (
        <section data-testid="cms-editor" className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="overline text-[#64748B]">Éditeur de contenu</div>
                    <div className="font-serif text-2xl md:text-3xl">
                        {PAGES.find((p) => p.slug === slug)?.label} · {LOCALE_LABELS[locale]}
                    </div>
                    {data?.updated_at && (
                        <div className="text-xs text-[#64748B] mt-1">
                            Dernière modification : {new Date(data.updated_at).toLocaleString("fr-BE")}
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="cms-page-select" className="text-sm px-4 py-3 border border-[#1E3A5F]/25 bg-white">
                        {PAGES.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
                    </select>
                    <select value={locale} onChange={(e) => setLocale(e.target.value)} data-testid="cms-locale-select" className="text-sm px-4 py-3 border border-[#1E3A5F]/25 bg-white">
                        {availableLocales.map((l) => <option key={l} value={l}>{LOCALE_LABELS[l]}</option>)}
                    </select>
                    <button onClick={openPreview} data-testid="cms-preview" className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors inline-flex items-center gap-2"><Eye size={16} strokeWidth={1.5} /> Aperçu</button>
                    <button onClick={discard} data-testid="cms-discard" className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-slate-100 transition-colors inline-flex items-center gap-2"><RotateCcw size={16} strokeWidth={1.5} /> Annuler</button>
                    <button onClick={save} disabled={saving} data-testid="cms-save" className="text-sm px-5 py-3 bg-white border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F2EE] transition-colors inline-flex items-center gap-2">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={1.5} />} Enregistrer
                    </button>
                    <button onClick={publish} disabled={publishing} data-testid="cms-publish" className="text-sm px-5 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                        {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} strokeWidth={1.5} />} Publier
                    </button>
                </div>
            </div>

            {/* SEO block */}
            <div className="border border-slate-200 bg-white">
                <button onClick={() => setShowSeo((v) => !v)} data-testid="cms-seo-toggle" className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                        <Search size={16} strokeWidth={1.5} className="text-[#DC2626]" />
                        <span className="font-serif text-xl text-[#1E3A5F]">SEO — {LOCALE_LABELS[locale]}</span>
                    </div>
                    <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform ${showSeo ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                    {showSeo && data && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                            <div className="px-6 pb-6 pt-2 space-y-6">
                                {(data.seo_schema || []).map((f) => (
                                    <div key={f.key}>
                                        <label className="overline text-[#64748B] flex items-center gap-3">
                                            {f.label}
                                            {isSeoChanged(f.key) && <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />}
                                        </label>
                                        {f.type === "textarea" ? (
                                            <textarea data-testid={`cms-seo-${f.key}`} className="field" rows={2} value={seoValues[f.key] ?? ""} onChange={(e) => setSeoField(f.key, e.target.value)} />
                                        ) : (
                                            <input data-testid={`cms-seo-${f.key}`} type={f.type === "image" ? "url" : "text"} className="field" value={seoValues[f.key] ?? ""} onChange={(e) => setSeoField(f.key, e.target.value)} />
                                        )}
                                        {f.type === "image" && seoValues[f.key] && (
                                            <img src={seoValues[f.key]} alt={f.label} className="mt-3 max-h-24 border border-slate-200" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Content groups */}
            <div className="space-y-3">
                {groups.map((g) => {
                    const isOpen = openGroup === g.name;
                    const changedCount = g.fields.filter((f) => isChanged(f.key)).length;
                    return (
                        <div key={g.name} className="border border-slate-200 bg-white">
                            <button onClick={() => setOpenGroup(isOpen ? null : g.name)} data-testid={`cms-group-${g.name}`} className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors">
                                <div className="flex items-center gap-3">
                                    <span className="font-serif text-xl text-[#1E3A5F]">{g.name}</span>
                                    {changedCount > 0 && (
                                        <span className="text-[10px] uppercase tracking-[0.2em] bg-[#DC2626]/10 text-[#DC2626] px-2 py-0.5 border border-[#DC2626]/30">
                                            {changedCount} modif.
                                        </span>
                                    )}
                                </div>
                                <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                                        <div className="px-6 pb-6 pt-2 space-y-6">
                                            {g.fields.map((f) => (
                                                <div key={f.key}>
                                                    <label className="overline text-[#64748B] flex items-center gap-3">
                                                        {f.label}
                                                        {isChanged(f.key) && <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />}
                                                    </label>
                                                    {f.type === "textarea" ? (
                                                        <textarea data-testid={`cms-field-${f.key}`} className="field" rows={3} value={values[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} />
                                                    ) : (
                                                        <input data-testid={`cms-field-${f.key}`} type={f.type === "image" ? "url" : "text"} className="field" value={values[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} />
                                                    )}
                                                    <div className="mt-1 text-xs text-slate-400">
                                                        Clé : <code className="font-mono">{f.key}</code>
                                                    </div>
                                                    {f.type === "image" && values[f.key] && (
                                                        <img src={values[f.key]} alt={f.label} className="mt-3 max-h-32 border border-slate-200" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>

            {anyChanged && (
                <div className="sticky bottom-4 z-10 border border-[#DC2626]/40 bg-white shadow-lg p-4 flex items-center justify-between">
                    <span className="text-sm">Modifications non publiées ({LOCALE_LABELS[locale]}).</span>
                    <div className="flex gap-2">
                        <button onClick={openPreview} className="text-sm px-4 py-2 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors inline-flex items-center gap-2"><Eye size={14} strokeWidth={1.5} /> Aperçu</button>
                        <button onClick={publish} disabled={publishing} className="text-sm px-4 py-2 bg-[#DC2626] text-white hover:bg-[#b91c1c] transition-colors inline-flex items-center gap-2">
                            {publishing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} strokeWidth={1.5} />} Publier
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
